/**
 * @m8t-stack/brain-engine — F4 librarian/codify persistence-guard (DESIGN S4/S5).
 *
 * Defense-in-depth check run on every CodifyDraft before it is forwarded to
 * the promote step. It is NOT the load-bearing control (the promote-side
 * structural allowlist is), but it catches the most obvious injection vectors:
 *
 *   1. References to the persona/loader surface (AGENTS.md, brain-loader,
 *      .m8t, persona) — a skill draft should never point at operator config.
 *   2. Standing-policy / privilege-grant language — reuses assertsStandingPolicy
 *      from the dreamer's apply.ts gate so the two layers stay in sync.
 *
 * Signature: guard(draft) → { ok: true } | { ok: false; reason: string }
 */

import { assertsStandingPolicy } from "../../dream/apply.js";

/** Fields inspected by the guard — body is required; the rest are optional. */
export interface GuardDraft {
  slug?: string;
  title?: string;
  body: string;
  /** Optional description field — scanned symmetrically with body/title/slug. */
  description?: string;
  /** Optional tags array — joined and scanned symmetrically with body/title/slug. */
  tags?: string[];
}

export type GuardResult = { ok: true } | { ok: false; reason: string };

/**
 * Substrings (case-insensitive) whose presence in slug, title, or body
 * indicates the draft is trying to reference the persona/loader surface.
 */
const PERSONA_LOADER_CUES = ["persona", "AGENTS.md", "brain-loader", "loader", ".m8t"];

/**
 * Normalize a string before running cue checks.
 *
 * NFKC decomposition collapses Unicode compatibility equivalents (e.g. full-width
 * "approve" variants). The character-class strip removes zero-width and invisible
 * Unicode spacers (U+200B ZERO WIDTH SPACE, U+200C ZERO WIDTH NON-JOINER,
 * U+200D ZERO WIDTH JOINER, U+FEFF BOM/ZWNBSP) that can split keywords invisibly
 * and evade a plain substring check. Consecutive whitespace is collapsed to a
 * single space so double-spacing tricks are also closed.
 *
 * Best-effort defense-in-depth. NFKC + zero-width stripping closes the cheap
 * evasions; full homoglyph/spaced-letter obfuscation is NOT closed here —
 * F5's human/eval gate is the real content backstop.
 */
// Strip zero-width / invisible Unicode spacers using individual \u escapes.
// We deliberately avoid a character-class range (ESLint no-misleading-character-class
// fires on ranges that span ZWJ/ZWNJ because they can join grapheme clusters).
// Covers U+200B (ZWSP), U+200C (ZWNJ), U+200D (ZWJ), U+FEFF (BOM/ZWNBSP).
const ZERO_WIDTH_RE = new RegExp("\u200b|\u200c|\u200d|\ufeff", "g");

const norm = (s: string): string =>
  s
    .normalize("NFKC")
    .replace(ZERO_WIDTH_RE, "")
    .replace(/[ \t]{2,}/g, " ");

function containsPersonaLoaderCue(s: string): boolean {
  const lower = norm(s).toLowerCase();
  return PERSONA_LOADER_CUES.some((cue) => lower.includes(cue.toLowerCase()));
}

/**
 * Returns { ok: false, reason } if the draft references the persona/loader/AGENTS.md/.m8t
 * surface, or asserts a standing policy/privilege grant; otherwise { ok: true }.
 *
 * This is defense-in-depth only — the load-bearing control is the promote-side
 * structural allowlist (a later task).
 */
export function guard(draft: GuardDraft): GuardResult {
  const tagsJoined = (draft.tags ?? []).join(" ");
  const fieldsToScan = [draft.body, draft.title ?? "", draft.slug ?? "", draft.description ?? "", tagsJoined];

  if (fieldsToScan.some(containsPersonaLoaderCue)) {
    return {
      ok: false,
      reason: "references the persona/loader/AGENTS.md/.m8t (persistence-guard)",
    };
  }

  if (assertsStandingPolicy(norm(draft.body))) {
    return {
      ok: false,
      reason: "asserts a standing policy/privilege (persistence-guard)",
    };
  }

  return { ok: true };
}
